import { useEffect, useState } from "react";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import { useHistory } from "react-router-dom";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { purchaseAdviceQuoteViewFields } from "../ui/LmsSharedForms";
import LmsTable from "../ui/LmsTable";
import { converToStartDateToEndDateString } from "../../../Helpers";

const PurchaseAdviceTransactionsTable = () => (
  <LmsTable
    actionButtons={["edit", "remove", "export"]}
    apiSuffix="purchasequotes/purchase-advice-transactions"
    deleteSuffix="purchasequotes/purchase-advice-transactions"
    sortable={["Created On"]}
    columnLabelsDictionary={{
      saleTypeOption: "Sale Type",
      statusOption: "Status",
      recognitionOption: "Revenue Recognition Method"
    }}
    columnClickOptions={{
      loan: {
        path: "servicing-purchase-advice-transactions/edit",
        idProp: "id",
      },
    }}
  />
);

const EditPurchaseAdviceTransactionsForm = ({ id }) => {
  const [{ data: adviceTransactionsData, isLoading, error, fetchData }] = useFetchData(`purchasequotes/initial-data-for-edit/${id}`); 
  const [inputsCopy, setInputsCopy] = useState(undefined as any);
  const [dueToBcData, setDueToBcData] = useState(undefined as any);
  const formatSubmitData = (inputs) => {
    return {
      ...inputs,
      "purchaseRequestDate": inputs?.purchaseDate,
      "payoutReceived": inputs?.receivedAmount,
      "recognitionOption": adviceTransactionsData?.recognitionOption,
      "purchaseQuoteLoanTransactionTempId": inputs?.purchaseQuoteLoanTransactionTempId,
      "revenueRecognitionTempId": inputs?.revenueRecognitionTempId,
    };
  };

  const handleBackToPurchaseAdviceTransactionsTable = () => {
    const history = useHistory();
    history.push("/servicing-loan-sale-approvals");
  };

  const handleGetPurchasersTotalPurchase = () => {
    if (adviceTransactionsData?.saleTypeOption) {
      if (adviceTransactionsData?.saleTypeOption === "Transfer fund to fund") {
        return adviceTransactionsData?.totalAmount;
      }

      return adviceTransactionsData?.totalPurchaseAmount;
    }
  };

  const myForm = purchaseAdviceQuoteViewFields(
    new BCFormController({ 
      ...adviceTransactionsData, 
      "dueToBc": dueToBcData,
      "purchasersTotalPurchase": handleGetPurchasersTotalPurchase(),
      "payoutResidual": handleGetPurchasersTotalPurchase() - adviceTransactionsData?.receivedAmount
    }, 
    { 
      values: { 
        loanId: adviceTransactionsData?.loanId,
        loanComments: adviceTransactionsData?.loanComments,
        revenueRecognitionTempId: adviceTransactionsData?.revenueRecognitionTempId,
        purchaseQuoteLoanTransactionTempId: adviceTransactionsData?.purchaseQuoteLoanTransactionTempId,
        buildersCapitalPercentageId: adviceTransactionsData?.buildersCapitalPercentageId,
        notionalBuildersCapitalPercentageId: adviceTransactionsData?.notionalBuildersCapitalPercentageId,
        pdPeriod1: converToStartDateToEndDateString(adviceTransactionsData?.pdStartDate1, adviceTransactionsData?.pdEndDate1),
        pdPeriod2: converToStartDateToEndDateString(adviceTransactionsData?.pdStartDate2, adviceTransactionsData?.pdEndDate2),
        isPdPeriod1Display: (adviceTransactionsData?.pdStartDate1 !== null && adviceTransactionsData?.pdEndDate1 !== null),
        isPdPeriod2Display: (adviceTransactionsData?.pdStartDate2 !== null && adviceTransactionsData?.pdEndDate2 !== null)
      },
      submitSuffix: "purchasequotes/complete-purchase-advice-quote", 
      submitMethod: "POST",
      dataFormatterFunc: formatSubmitData,
      onSubmitSuccess: handleBackToPurchaseAdviceTransactionsTable
    }),
    false,
    false
  );

  const onAdviceTransactionFieldChanged = (options, lastUpdated) => {
    const inputCopy = {...options?.inputs};
    if (lastUpdated === "purchaseDate") {
      inputCopy[lastUpdated] = options?.inputs[lastUpdated];
      inputCopy.showRevenueRegenerateMessage = true;
    }

    if (lastUpdated === "amortizationOption") {
      inputCopy[lastUpdated] = options?.inputs[lastUpdated];
      inputCopy.showRevenueRegenerateMessage = true;
    }

    if (lastUpdated === "saleTypeOption") {
      let purchasersTotalPurchase;
      if (options?.inputs[lastUpdated] === "Transfer fund to fund") {
        purchasersTotalPurchase = adviceTransactionsData?.totalAmount || 0;
      }
      else {
        purchasersTotalPurchase = adviceTransactionsData?.totalPurchaseAmount || 0;
      }

      inputCopy.saleTypeOption = options?.inputs[lastUpdated];
      inputCopy.purchasersTotalPurchase = purchasersTotalPurchase;
      inputCopy.payoutResidual = options?.inputs.receivedAmount ? purchasersTotalPurchase - options?.inputs.receivedAmount : -purchasersTotalPurchase;
    }

    if (lastUpdated === "receivedAmount") {
      inputCopy.receivedAmount = options?.inputs[lastUpdated];
      inputCopy.payoutResidual = options?.inputs.purchasersTotalPurchase - options?.inputs[lastUpdated];
    }

    if (lastUpdated === "buildersCapitalPercentageId") {
      inputCopy.showRevenueRegenerateMessage = true;
      inputCopy.buildersCapitalPercentageId = options?.inputs[lastUpdated];
    }

    if (lastUpdated === "notionalBuildersCapitalPercentageId") {
      inputCopy.notionalBuildersCapitalPercentageId = options?.inputs[lastUpdated];
    }

    setInputsCopy({ ...inputCopy });
  };

  const handleCompletePurchaseAdvice = (inputs, options) => {
    if (inputs?.showRevenueRegenerateMessage) {
      options.setFormErrorMessage({ message: "Please regenerate Earned/Unearned Revenue." });
    }
    else {
      options.handleSubmit("purchasequotes/complete-purchase-advice-quote", { 
        method: "POST", 
        data: formatSubmitData(inputs), 
        buttonText: options.buttonText,
        onSuccessFunc: () => {options.onSuccessFunc(); options.useFormInstance.history.back();}
      });
    }
  };

  useEffect(() => {
    if (adviceTransactionsData) {
      if (adviceTransactionsData?.purchaseQuoteLoanTransactionTempId) {
        const getDueToBcValue = async () => {
          const { data: dueToBcValue } = await fetchData(`purchasequotes/due-to-bc/${adviceTransactionsData?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET" });
          setDueToBcData(dueToBcValue?.dueToBc);
        };
        
        getDueToBcValue();
      }
    }
  }, [adviceTransactionsData]);

  return <LmsFormGenerator
    isLoading={isLoading}
    error={error}
    myForm={myForm}
    inputs={inputsCopy}
    actions={[{ text: "Complete Purchase Advice", action: "save" }, "close"]}
    onFormChanged={onAdviceTransactionFieldChanged}
    handleSubmit={handleCompletePurchaseAdvice}
  />;
};

const LmsServicingPurchaseAdviceTransactions = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params);
  if (!isActionAllowed) {
    history.goBack();
  }

  return (<>
    {isMainPage && <PurchaseAdviceTransactionsTable/>}
    {isEdit && <EditPurchaseAdviceTransactionsForm id={id}/>}
  </>);
};

export default LmsServicingPurchaseAdviceTransactions;